/**
 * Maps common backend HTTPException messages to localized strings.
 *
 * The backend returns English `detail` strings for ~113 endpoints. Rewriting
 * the backend to return AR/EN pairs is invasive; instead we translate at the
 * boundary where errors meet the user (toast/UI) using a lookup table.
 *
 * Usage:
 *   import { localizeError } from "@/lib/errors";
 *   toast.error(localizeError(e, t, lang));
 *
 * Falls back to the raw backend detail if no mapping is found.
 */

// Direct phrase → translation-key mapping for the most common errors.
const DIRECT_KEY_MAP = {
  // Validation
  "Required": "required_field",
  "No items": "no_items",
  "Invalid input": "invalid_input",
  "Phone (to) required": "phone_required",
  "Message body is required": "message_body_required",
  // Resources
  "Product not found": "product_not_found",
  "employee not found": "employee_not_found",
  "Customer not found": "customer_not_found",
  "Service not found": "service_not_found",
  "Package not found": "package_not_found",
  "Invoice not found": "invoice_not_found",
  "Branch not found": "branch_not_found",
  "Account not found": "account_not_found",
  // Business rules
  "branch_id is required": "branch_required",
  "Invalid branch_id": "invalid_branch_id",
  "No recipients matched the selected segment": "no_recipients_matched",
  "Employee with this national ID already exists": "duplicate_national_id",
  "Insufficient stock": "insufficient_stock",
  // Auth
  "Invalid credentials": "invalid_credentials",
  "Not authenticated": "not_authenticated",
  "Forbidden": "forbidden",
  // Generic
  "Error": "error_generic",
  "Internal Server Error": "error_generic",
};

// Patterns that need extraction (e.g. "Invalid employee_user_id(s): ['xxx']")
const PATTERN_MAP = [
  {
    pattern: /^Invalid employee_user_id\(s\)/i,
    key: "invalid_employee_user_id",
  },
];

/**
 * Returns the localized error string for a thrown axios error or raw detail.
 * Always returns a non-empty string suitable for `toast.error(...)`.
 */
export function localizeError(err, t, lang) {
  // 1. Structured detail (e.g. plan_quota_exceeded) — preferred, the backend
  //    already provided bilingual messages, just pick the right locale.
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.code) {
    const isAr = lang === "ar";
    if (detail.code === "plan_quota_exceeded" || detail.code === "plan_page_not_included") {
      return isAr ? (detail.message_ar || detail.message_en) : (detail.message_en || detail.message_ar);
    }
    // Unknown structured code — fall back to whichever locale message is
    // present, otherwise the code itself.
    return (isAr ? detail.message_ar : detail.message_en) || detail.message_en || detail.message_ar || detail.code;
  }

  let raw = "";
  if (typeof err === "string") raw = err;
  else if (detail) raw = String(detail);
  else if (err?.message) raw = err.message;
  if (!raw) return t("error_generic");

  // Exact match
  if (DIRECT_KEY_MAP[raw]) return t(DIRECT_KEY_MAP[raw]);
  // Case-insensitive exact match
  const rawLow = raw.toLowerCase();
  for (const k of Object.keys(DIRECT_KEY_MAP)) {
    if (k.toLowerCase() === rawLow) return t(DIRECT_KEY_MAP[k]);
  }
  // Regex patterns
  for (const { pattern, key } of PATTERN_MAP) {
    if (pattern.test(raw)) return t(key);
  }
  // No match → return raw (still better than silent failure)
  return raw;
}
